import React, { useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import Button from '../ui/Button';

const SearchOverlay = ({ isOpen, onClose, searchContext, onSearch }) => {
  // 表单字段引用
  const studentNameRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const courseNameRef = useRef(null);
  
  // 学生状态选择
  const [selectedStudentStatus, setSelectedStudentStatus] = useState(null);
  // 课程状态选择
  const [selectedCourseStatus, setSelectedCourseStatus] = useState(null);
  // 记录状态选择
  const [selectedRecordStatus, setSelectedRecordStatus] = useState('ACTIVE');

  if (!isOpen) return null;

  const handleSearch = () => {
    const searchParams = {};
    
    // 根据搜索上下文收集不同的参数
    if (searchContext === 'record' || searchContext === 'finance') {
      if (studentNameRef.current?.value) {
        searchParams.student_name = studentNameRef.current.value;
      }
      if (startDateRef.current?.value) {
        searchParams.date_from = startDateRef.current.value;
      }
      if (endDateRef.current?.value) {
        searchParams.date_to = endDateRef.current.value;
      }
      if (searchContext === 'record' && selectedRecordStatus) {
        if (selectedRecordStatus !== 'all') {
          searchParams.status = selectedRecordStatus;
        } else {
          searchParams.status = 'all'; // Keep it as 'all' but we'll handle it in HomeView
        }
      }
    }
    
    if (searchContext === 'student') {
      if (studentNameRef.current?.value) {
        searchParams.name = studentNameRef.current.value;
      }
      // 明确传递学生状态：null表示全部（传空字符串），其他值正常传递
      if (selectedStudentStatus) {
        searchParams.student_type = selectedStudentStatus;
      } else {
        // 选择"全部"时传递空字符串，表示不筛选状态
        searchParams.student_type = '';
      }
    }
    
    if (searchContext === 'course') {
      if (courseNameRef.current?.value) {
        searchParams.name = courseNameRef.current.value;
      }
      if (selectedCourseStatus) {
        searchParams.status = selectedCourseStatus;
      } else {
        searchParams.status = '';
      }
    }
    
    // 调用搜索回调
    if (onSearch) {
      onSearch(searchParams);
    }
    
    onClose();
  };

  return (
    <div className="absolute inset-0 z-[60] flex flex-col">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="bg-white w-full rounded-b-3xl shadow-xl z-10 animate-in slide-in-from-top duration-300">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="font-bold text-[#2D3748]">
            {searchContext === 'record' ? '筛选上课记录' : 
             searchContext === 'student' ? '搜索学生' : 
             searchContext === 'finance' ? '筛选账务记录' : '搜索课程'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        <div className="p-5 space-y-4">
          {/* Conditional Fields */}
          {(searchContext === 'record' || searchContext === 'finance') && (
            <>
              <div>
                <label className="text-xs font-medium text-[#718096] mb-1.5 block">学生姓名</label>
                <div className="flex items-center bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200">
                  <Search size={16} className="text-gray-400 mr-2" />
                  <input 
                    ref={studentNameRef}
                    type="text" 
                    placeholder="输入学生姓名" 
                    className="bg-transparent text-sm w-full outline-none text-[#2D3748]" 
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#718096] mb-1.5 block">日期范围</label>
                <div className="flex gap-2 items-center">
                  <input 
                    ref={startDateRef}
                    type="date" 
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#4A5568] outline-none" 
                  />
                  <span className="text-gray-400">-</span>
                  <input 
                    ref={endDateRef}
                    type="date" 
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#4A5568] outline-none" 
                  />
                </div>
              </div>
              {searchContext === 'record' && (
                <div>
                  <label className="text-xs font-medium text-[#718096] mb-1.5 block">记录状态</label>
                  <div className="flex gap-2">
                    <button 
                      type="button"
                      onClick={() => setSelectedRecordStatus('ACTIVE')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedRecordStatus === 'ACTIVE'
                          ? 'bg-[#3182CE] text-white'
                          : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      正常记录
                    </button>
                    <button 
                      type="button"
                      onClick={() => setSelectedRecordStatus('all')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedRecordStatus === 'all'
                          ? 'bg-[#3182CE] text-white'
                          : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      全部记录
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {searchContext === 'student' && (
            <>
              <div>
                <label className="text-xs font-medium text-[#718096] mb-1.5 block">学生姓名</label>
                <div className="flex items-center bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200">
                  <Search size={16} className="text-gray-400 mr-2" />
                  <input 
                    ref={studentNameRef}
                    type="text" 
                    placeholder="输入学生姓名" 
                    className="bg-transparent text-sm w-full outline-none text-[#2D3748]" 
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#718096] mb-1.5 block">状态</label>
                <div className="flex gap-2">
                  <button 
                    type="button"
                    onClick={() => setSelectedStudentStatus(null)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedStudentStatus === null
                        ? 'bg-[#3182CE] text-white'
                        : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    全部
                  </button>
                  <button 
                    type="button"
                    onClick={() => setSelectedStudentStatus('在学')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedStudentStatus === '在学'
                        ? 'bg-[#3182CE] text-white'
                        : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    在学
                  </button>
                  <button 
                    type="button"
                    onClick={() => setSelectedStudentStatus('潜在')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedStudentStatus === '潜在'
                        ? 'bg-[#3182CE] text-white'
                        : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    潜在
                  </button>
                  <button 
                    type="button"
                    onClick={() => setSelectedStudentStatus('已结束')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedStudentStatus === '已结束'
                        ? 'bg-[#3182CE] text-white'
                        : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    已结束
                  </button>
                </div>
              </div>
            </>
          )}

          {searchContext === 'course' && (
            <>
              <div>
                <label className="text-xs font-medium text-[#718096] mb-1.5 block">课程名称</label>
                <div className="flex items-center bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-200">
                  <Search size={16} className="text-gray-400 mr-2" />
                  <input 
                    ref={courseNameRef}
                    type="text" 
                    placeholder="输入课程名称" 
                    className="bg-transparent text-sm w-full outline-none text-[#2D3748]" 
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#718096] mb-1.5 block">状态</label>
                <div className="flex gap-2">
                  <button 
                    type="button"
                    onClick={() => setSelectedCourseStatus(null)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCourseStatus === null
                        ? 'bg-[#3182CE] text-white'
                        : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    全部
                  </button>
                  <button 
                    type="button"
                    onClick={() => setSelectedCourseStatus('可用')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCourseStatus === '可用'
                        ? 'bg-[#3182CE] text-white'
                        : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    可用
                  </button>
                  <button 
                    type="button"
                    onClick={() => setSelectedCourseStatus('下线')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCourseStatus === '下线'
                        ? 'bg-[#3182CE] text-white'
                        : 'bg-gray-50 text-[#4A5568] border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    下线
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="pt-2">
             <Button className="w-full h-11 text-base" onClick={handleSearch}>查询</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
